import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useParameterSearch } from "@/hooks/use-parameters";
import { useParameterColumns } from "@/components/parameters/parameter-columns";
import {
  ParameterFilters,
  type ParameterFilterValues,
} from "@/components/parameters/parameter-filters";
import { ParameterEmptyState } from "@/components/parameters/parameter-empty-state";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  SkeletonTable,
  Breadcrumbs,
} from "@/components/ui";

type Parameter = components["schemas"]["Parameter"];
type ParameterCategory = components["schemas"]["ParameterCategory"];

export const Route = createFileRoute("/_authenticated/parameters/")({
  component: ParameterBrowserPage,
});

export interface ParameterBrowserProps {
  onSelect?: (param: Parameter) => void;
  prefixFilter?: string;
  categoryFilter?: string;
  excludeIds?: string[];
}

const PAGE_SIZE = 20;

function ParameterBrowserPage() {
  return <ParameterBrowser />;
}

export function ParameterBrowser({
  onSelect,
  prefixFilter,
  categoryFilter,
  excludeIds,
}: ParameterBrowserProps = {}) {
  const { t } = useTranslation("parameters");
  const navigate = useNavigate();
  const isPicker = !!onSelect;

  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("parameters", "create");

  const [filters, setFilters] = useState<ParameterFilterValues>({
    prefix: prefixFilter,
    category: categoryFilter,
  });
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: false }]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as "code" | "name" | "category" | undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  // Reset offset when filters or sorting change.
  useEffect(() => {
    setOffset(0);
  }, [filters, sorting]);

  const columns = useParameterColumns();

  const { data, isLoading } = useParameterSearch(orgId, {
    q: filters.search,
    prefix: filters.prefix,
    category: filters.category as ParameterCategory | undefined,
    source: filters.source as "system" | "custom" | undefined,
    sort: sortBy,
    order: sortDir,
    limit: PAGE_SIZE,
    offset,
  });

  const allResults = data?.parameters ?? [];
  const results = excludeIds ? allResults.filter((p) => !excludeIds.includes(p.id)) : allResults;
  const total = data?.total ?? 0;

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const hasFilters = !!(filters.search || filters.prefix || filters.category || filters.source);

  const handleRowClick = (row: Parameter) => {
    if (onSelect) {
      onSelect(row);
      return;
    }
    void navigate({
      to: "/parameters/$parameterId",
      params: { parameterId: row.id },
    });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className={isPicker ? "" : "p-8"}>
      {!isPicker && (
        <>
          <Breadcrumbs items={[{ label: t("title") }]} />
          <PageHeader
            title={t("title")}
            actions={
              canCreate ? (
                <Button
                  onClick={() =>
                    void navigate({
                      to: "/parameters/new",
                    })
                  }
                >
                  {t("actions.addCustom")}
                </Button>
              ) : undefined
            }
          />
        </>
      )}

      <div className="mt-6">
        <ParameterFilters
          filters={filters}
          onChange={(f) => {
            setFilters(f);
            setOffset(0);
          }}
        />
      </div>

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={6} rows={5} />
        </div>
      ) : results.length === 0 ? (
        <div className="mt-6">
          <ParameterEmptyState
            hasFilters={hasFilters}
            onAddCustom={
              isPicker
                ? undefined
                : () =>
                    void navigate({
                      to: "/parameters/new",
                    })
            }
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={results}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
